import { useEffect, useState } from 'react';
import { loadPricesSnapshot, type PricesSnapshot } from '../libs/publicPricesSnapshot';
import { getSsrData } from '../libs/ssrData';

export interface UsePricesSnapshot {
  data: PricesSnapshot | null;
  loading: boolean;
  error: string | null;
}

/**
 * Hook para cargar el snapshot de precios públicos.
 * `error` se setea también cuando el snapshot existe pero viene `available:false`
 * (datos no disponibles), para que la UI pueda mostrar el aviso.
 */
export function usePricesSnapshot(): UsePricesSnapshot {
  // En el render estático del build los precios vienen precargados (ver libs/ssrData).
  const [data, setData] = useState<PricesSnapshot | null>(() => getSsrData<PricesSnapshot>('prices') ?? null);
  const [loading, setLoading] = useState(() => !getSsrData('prices'));
  const [error, setError] = useState<string | null>(() => {
    const ssr = getSsrData<PricesSnapshot>('prices');
    return ssr && !ssr.available ? 'Precios de referencia temporalmente no disponibles.' : null;
  });

  useEffect(() => {
    let active = true;
    setLoading(true);
    loadPricesSnapshot()
      .then((snap) => {
        if (!active) return;
        setData(snap);
        setError(snap.available ? null : 'Precios de referencia temporalmente no disponibles.');
      })
      .catch(() => {
        if (active) setError('No se pudieron cargar los precios.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return { data, loading, error };
}
